import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Save, RotateCcw, Eye, Loader, Palette, Type, Image,
  MessageSquare, Video, Rocket, Settings2, CheckCircle,
  Plus, Trash2, GripVertical
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminHomeEditor = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/home-config`, { withCredentials: true });
      setConfig(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFeatureChange = (field, index, value) => {
    setConfig(prev => {
      const newFeatures = [...(prev[field] || [])];
      newFeatures[index] = value;
      return { ...prev, [field]: newFeatures };
    });
    setHasChanges(true);
  };

  const addFeature = (field) => {
    setConfig(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), 'Nova funcionalidade']
    }));
    setHasChanges(true);
  };

  const removeFeature = (field, index) => {
    setConfig(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/home-config`,
        config,
        { withCredentials: true }
      );
      toast.success('Configuração salva!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Tem certeza que deseja resetar para as configurações padrão? Esta ação não pode ser desfeita.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/home-config/reset`,
        {},
        { withCredentials: true }
      );
      setConfig(response.data.config);
      toast.success('Configuração resetada!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao resetar');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    window.open('/', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="home-editor-title">
              Editor da Home Page
            </h1>
            <p className="text-lg text-gray-600">
              Personalize textos, cores e seções da página inicial
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={openPreview}
              data-testid="preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Visualizar
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Resetar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 text-sm">Você tem alterações não salvas</span>
          </div>
        )}

        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="hero" className="data-[state=active]:bg-indigo-100">
              <Rocket className="w-4 h-4 mr-2" />
              Hero
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-indigo-100">
              <Settings2 className="w-4 h-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="data-[state=active]:bg-indigo-100">
              <MessageSquare className="w-4 h-4 mr-2" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-indigo-100">
              <Video className="w-4 h-4 mr-2" />
              Conteúdo IA
            </TabsTrigger>
            <TabsTrigger value="cta" className="data-[state=active]:bg-indigo-100">
              <CheckCircle className="w-4 h-4 mr-2" />
              CTA Final
            </TabsTrigger>
            <TabsTrigger value="colors" className="data-[state=active]:bg-indigo-100">
              <Palette className="w-4 h-4 mr-2" />
              Cores
            </TabsTrigger>
          </TabsList>

          {/* Hero Section */}
          <TabsContent value="hero" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Seção Principal (Hero)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Badge (texto pequeno acima do título)</Label>
                <Input
                  value={config?.hero_badge_text || ''}
                  onChange={(e) => handleChange('hero_badge_text', e.target.value)}
                  placeholder="IA que vende enquanto você dorme"
                  data-testid="hero-badge-input"
                />
              </div>

              <div>
                <Label>Título - Linha 1</Label>
                <Input
                  value={config?.hero_title_line1 || ''}
                  onChange={(e) => handleChange('hero_title_line1', e.target.value)}
                  placeholder="Venda no"
                />
              </div>

              <div>
                <Label>Título - Destaque 1 (colorido)</Label>
                <Input
                  value={config?.hero_title_highlight1 || ''}
                  onChange={(e) => handleChange('hero_title_highlight1', e.target.value)}
                  placeholder="WhatsApp"
                />
              </div>

              <div>
                <Label>Título - Linha 2</Label>
                <Input
                  value={config?.hero_title_line2 || ''}
                  onChange={(e) => handleChange('hero_title_line2', e.target.value)}
                  placeholder="com"
                />
              </div>

              <div>
                <Label>Título - Destaque 2 (colorido)</Label>
                <Input
                  value={config?.hero_title_highlight2 || ''}
                  onChange={(e) => handleChange('hero_title_highlight2', e.target.value)}
                  placeholder="Inteligência Artificial"
                />
              </div>

              <div className="md:col-span-2">
                <Label>Subtítulo</Label>
                <Textarea
                  value={config?.hero_subtitle || ''}
                  onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                  placeholder="Sua loja digital completa..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Botão Principal</Label>
                <Input
                  value={config?.hero_cta_primary || ''}
                  onChange={(e) => handleChange('hero_cta_primary', e.target.value)}
                  placeholder="Começar Grátis"
                />
              </div>

              <div>
                <Label>Botão Secundário</Label>
                <Input
                  value={config?.hero_cta_secondary || ''}
                  onChange={(e) => handleChange('hero_cta_secondary', e.target.value)}
                  placeholder="Já tenho conta"
                />
              </div>
            </div>
          </TabsContent>

          {/* Features Section */}
          <TabsContent value="features" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Seção de Funcionalidades</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Título da Seção</Label>
                  <Input
                    value={config?.features_title || ''}
                    onChange={(e) => handleChange('features_title', e.target.value)}
                    placeholder="Tudo que você precisa para"
                  />
                </div>
                <div>
                  <Label>Título - Destaque</Label>
                  <Input
                    value={config?.features_title_highlight || ''}
                    onChange={(e) => handleChange('features_title_highlight', e.target.value)}
                    placeholder="vender mais"
                  />
                </div>
              </div>

              <div>
                <Label>Subtítulo</Label>
                <Input
                  value={config?.features_subtitle || ''}
                  onChange={(e) => handleChange('features_subtitle', e.target.value)}
                  placeholder="Ferramentas profissionais..."
                />
              </div>
            </div>
          </TabsContent>

          {/* WhatsApp Section */}
          <TabsContent value="whatsapp" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Seção WhatsApp IA</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Visível</span>
                <Switch
                  checked={config?.whatsapp_visible !== false}
                  onCheckedChange={(checked) => handleChange('whatsapp_visible', checked)}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Badge</Label>
                  <Input
                    value={config?.whatsapp_badge || ''}
                    onChange={(e) => handleChange('whatsapp_badge', e.target.value)}
                    placeholder="Automação WhatsApp"
                  />
                </div>
                <div>
                  <Label>Título</Label>
                  <Input
                    value={config?.whatsapp_title || ''}
                    onChange={(e) => handleChange('whatsapp_title', e.target.value)}
                    placeholder="Seu assistente de vendas que"
                  />
                </div>
              </div>

              <div>
                <Label>Título - Destaque</Label>
                <Input
                  value={config?.whatsapp_title_highlight || ''}
                  onChange={(e) => handleChange('whatsapp_title_highlight', e.target.value)}
                  placeholder="nunca para"
                />
              </div>

              <div>
                <Label>Lista de Funcionalidades</Label>
                <div className="space-y-2 mt-2">
                  {(config?.whatsapp_features || []).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <Input
                        value={feature}
                        onChange={(e) => handleFeatureChange('whatsapp_features', index, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature('whatsapp_features', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addFeature('whatsapp_features')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Item
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Content Creator Section */}
          <TabsContent value="content" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Seção Criador de Conteúdo IA</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Visível</span>
                <Switch
                  checked={config?.content_visible !== false}
                  onCheckedChange={(checked) => handleChange('content_visible', checked)}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Badge</Label>
                  <Input
                    value={config?.content_badge || ''}
                    onChange={(e) => handleChange('content_badge', e.target.value)}
                    placeholder="Criador de Conteúdo IA"
                  />
                </div>
                <div>
                  <Label>Título</Label>
                  <Input
                    value={config?.content_title || ''}
                    onChange={(e) => handleChange('content_title', e.target.value)}
                    placeholder="Crie vídeos profissionais"
                  />
                </div>
              </div>

              <div>
                <Label>Título - Destaque</Label>
                <Input
                  value={config?.content_title_highlight || ''}
                  onChange={(e) => handleChange('content_title_highlight', e.target.value)}
                  placeholder="sem aparecer"
                />
              </div>

              <div>
                <Label>Lista de Funcionalidades</Label>
                <div className="space-y-2 mt-2">
                  {(config?.content_features || []).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <Input
                        value={feature}
                        onChange={(e) => handleFeatureChange('content_features', index, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature('content_features', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addFeature('content_features')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Item
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* CTA Section */}
          <TabsContent value="cta" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Seção CTA Final</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Visível</span>
                <Switch
                  checked={config?.cta_visible !== false}
                  onCheckedChange={(checked) => handleChange('cta_visible', checked)}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={config?.cta_title || ''}
                  onChange={(e) => handleChange('cta_title', e.target.value)}
                  placeholder="Pronto para automatizar suas vendas?"
                />
              </div>

              <div>
                <Label>Subtítulo</Label>
                <Input
                  value={config?.cta_subtitle || ''}
                  onChange={(e) => handleChange('cta_subtitle', e.target.value)}
                  placeholder="Junte-se a milhares de vendedores..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Botão Principal</Label>
                  <Input
                    value={config?.cta_button_primary || ''}
                    onChange={(e) => handleChange('cta_button_primary', e.target.value)}
                    placeholder="Criar Conta Grátis"
                  />
                </div>
                <div>
                  <Label>Botão Secundário</Label>
                  <Input
                    value={config?.cta_button_secondary || ''}
                    onChange={(e) => handleChange('cta_button_secondary', e.target.value)}
                    placeholder="Acessar Loja"
                  />
                </div>
              </div>

              <div>
                <Label>Texto do Rodapé</Label>
                <Input
                  value={config?.footer_text || ''}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  placeholder="© 2024 Needi. Todos os direitos reservados."
                />
              </div>
            </div>
          </TabsContent>

          {/* Colors Section */}
          <TabsContent value="colors" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cores do Tema</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>Cor Primária</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={config?.primary_color || '#4f46e5'}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
                  />
                  <Input
                    value={config?.primary_color || '#4f46e5'}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    placeholder="#4f46e5"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Usado em botões e destaques principais</p>
              </div>

              <div>
                <Label>Cor Secundária</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={config?.secondary_color || '#7c3aed'}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
                  />
                  <Input
                    value={config?.secondary_color || '#7c3aed'}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    placeholder="#7c3aed"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Usado em gradientes e elementos secundários</p>
              </div>

              <div>
                <Label>Cor de Destaque (Accent)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={config?.accent_color || '#10b981'}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
                  />
                  <Input
                    value={config?.accent_color || '#10b981'}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    placeholder="#10b981"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Usado em CTAs e elementos de destaque (verde)</p>
              </div>
            </div>

            {/* Preview das cores */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-900 mb-4">Preview</h4>
              <div className="flex items-center gap-4">
                <button 
                  className="px-6 py-3 rounded-lg text-white font-medium"
                  style={{ backgroundColor: config?.primary_color || '#4f46e5' }}
                >
                  Botão Primário
                </button>
                <button 
                  className="px-6 py-3 rounded-lg text-white font-medium"
                  style={{ backgroundColor: config?.secondary_color || '#7c3aed' }}
                >
                  Botão Secundário
                </button>
                <button 
                  className="px-6 py-3 rounded-lg text-white font-medium"
                  style={{ backgroundColor: config?.accent_color || '#10b981' }}
                >
                  Botão Accent
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Last update info */}
        {config?.updated_at && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Última atualização: {new Date(config.updated_at).toLocaleString('pt-BR')}
            {config.updated_by && ` por ${config.updated_by}`}
          </div>
        )}
      </div>
    </div>
  );
};
